/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Divider, Grid, InputAdornment } from '@material-ui/core';
import { Button, Card, Icons, TextField, Typography } from '@nutrien/cxp-components';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useMst } from '../../mobx-models/Root';
import {
  Deficiency,
  DeficiencySortByFilter,
  DeficiencyView,
  Discipline,
} from '../../models/models';
import useConditionRating from '../../rxdb/conditionRating/useConditionRating';
import { DeficiencyList } from '../../rxdb/deficiencyList/queryBuilder';
import useDeficiencies from '../../rxdb/deficiencyList/useDeficiencies';
import { useDeficiencyList } from '../../rxdb/deficiencyList/useDeficiencyList';
import useSelectedInspection from '../../rxdb/inspectionList/useSelectedInspection';
import useRepairFactor from '../../rxdb/repairFactor/useRepairFactor';
import useAccessOnTask from '../../rxdb/taskList/useAccessOnTask';
import useUrgency from '../../rxdb/urgency/useUrgency';
import { sortHelper, useNotification } from '../../utilities';
import { DEFICIENCY_SORTBY_DEFAULT_VALUE } from '../../utilities/constants';
import { ComponentInfo } from '../../utilities/enums';
import { handleSortByValueChange } from '../../utilities/utilityFunctions';
import AddPhotoDialog from '../AddPhotoDialog';
import AuditLogs from '../AuditLogs';
import CardView from '../CardView/CardView';
import CondensedView from '../CondensedView';
import DeficienciesSortBy from '../DeficienciesSortBy';
import DeficienciesSortByPanel from '../DeficienciesSortByPanel';
import EditDeficiency from '../EditDeficiency';
import useStyles from './Deficiencies.styles';

dayjs.extend(utc);

const Deficiencies: React.FC = () => {
  const classes = useStyles();
  const { deficiencySortHelper } = sortHelper();
  const { errorNotification } = useNotification();
  const { configCatFlag, CurrentUserSelected, SiteDiscipline } = useMst();
  const { repairFactorListData } = useRepairFactor();
  const { saveDeficiencyList } = useDeficiencyList();
  const { selectedInspection } = useSelectedInspection(
    String(CurrentUserSelected.inspectionId),
    CurrentUserSelected.workPackageId,
  );
  const { deficiencies, activeDeficiencies } = useDeficiencies(
    CurrentUserSelected.taskId,
    CurrentUserSelected.inspectionId,
    Number(CurrentUserSelected.assetId),
    selectedInspection?.assetInspectionStatusId,
  );
  const { isEditable, taskData } = useAccessOnTask();

  const [addFlag, setAddFlag] = useState<boolean>(false);
  const [deficiencyFilters, setDeficiencyFilters] = useState<DeficiencySortByFilter>(
    DEFICIENCY_SORTBY_DEFAULT_VALUE,
  );
  const [view, setView] = useState<DeficiencyView>({ condensed: true, card: false });
  const [searchText, setSearchText] = useState<string>('');
  const [editDeficiencyDialog, setEditDeficiencyDialog] = useState<boolean>(false);
  const [editFile, setEditFile] = useState<Deficiency>();
  const [openAuditLogsDialog, setOpenAuditLogsDialog] = useState<boolean>(false);
  const [auditLogsDeficiencyId, setAuditLogsDeficiencyId] = useState<number>(0);
  const [anchorEl, setAnchorEl] = useState<HTMLInputElement | null>(null);
  const [tabValue, setValue] = useState<number>(0);
  const [inspectionIdForActiveDeficiency, setInspectionIdForActiveDeficiency] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filteredDeficiencies, setFilteredDeficiencies] = useState<DeficiencyList[]>([]);
  const [deficiencyListData, setDeficiencyListData] = useState<DeficiencyList[]>([]);
  const { conditionRatingListData } = useConditionRating(SiteDiscipline.disciplineId);
  const { urgencyListData } = useUrgency(SiteDiscipline.disciplineId);
  const [reloadCondensedView, setReloadCondensedView] = useState<boolean>(false);
  const isSortByOpen = Boolean(anchorEl);
  const id = isSortByOpen ? 'simple-popover' : '';

  const handleFilterChange = (fieldName: keyof DeficiencySortByFilter) => {
    setDeficiencyFilters((prev: DeficiencySortByFilter) => {
      const value = handleSortByValueChange(prev[fieldName]);
      return { ...DEFICIENCY_SORTBY_DEFAULT_VALUE, [fieldName]: value };
    });
  };

  const isDeficiencyVisible = (deficiency: Deficiency) =>
    deficiency?.name?.toLowerCase().includes(searchText?.toLowerCase()) ||
    deficiency?.componentFailureId?.includes(searchText);

  useEffect(() => {
    if (tabValue === 1) {
      setDeficiencyListData(activeDeficiencies);
    } else {
      setDeficiencyListData(deficiencies);
    }
  }, [activeDeficiencies, deficiencies, tabValue]);

  const fetchLatestData = (
    prevData: DeficiencyList[],
    newData: DeficiencyList[],
    deficiencyFilter: (deficiency: Deficiency) => boolean,
  ) => {
    const oldIds = prevData.map(deficiency => deficiency?.componentFailureId);
    const newIds = newData.map(deficiency => deficiency?.componentFailureId);
    const data = deficiencyListData
      .filter(
        deficiency =>
          oldIds.includes(deficiency?.componentFailureId) ||
          newIds.includes(deficiency?.componentFailureId),
      )
      .filter(deficiencyFilter)
      .sort((a, b) => Number(deficiencySortHelper(a, b, deficiencyFilters, tabValue === 1)));
    return data;
  };

  const getPaginatedData = (deficiencyFilter: (deficiency: Deficiency) => boolean) => {
    const filteredData = deficiencyListData
      .filter(deficiencyFilter)
      .sort((a, b) => Number(deficiencySortHelper(a, b, deficiencyFilters, tabValue === 1)));

    if (searchText) {
      return filteredData;
    }

    return filteredData.slice((currentPage - 1) * 10, currentPage * 10);
  };

  const handlePaginatedData = () => {
    if (deficiencyListData.length) {
      const newData = getPaginatedData(isDeficiencyVisible);
      setFilteredDeficiencies(pre => fetchLatestData(pre, newData, isDeficiencyVisible));
    }
  };

  useEffect(() => {
    handlePaginatedData();
  }, [currentPage, deficiencyListData]);

  const resetFilteredDeficiencies = () => {
    setFilteredDeficiencies([]);
    setCurrentPage(1);
    handlePaginatedData();
  };

  useEffect(() => {
    resetFilteredDeficiencies();
  }, [tabValue, searchText, deficiencyFilters, deficiencyListData]);

  const handleScroll = e => {
    const target = e.target;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;

    if (scrollHeight - scrollTop - clientHeight <= 10) {
      setCurrentPage(prevPage => prevPage + 1);
    }
  };

  const handleSortByClick = (event: React.MouseEvent<HTMLElement>) => {
    const value = event.currentTarget as unknown as null;
    setAnchorEl(value);
  };

  const handleClose = () => setAnchorEl(null);

  const resetSortBy = () => {};

  const handleSearch = (event: React.FocusEvent<HTMLInputElement>) => {
    const {
      target: { value },
    } = event;
    setSearchText(value);
  };

  const onChangeForCardView = async (deficiency: Deficiency) => {
    document.getElementById('addPhotoDeficiency')?.click();
    setEditFile(deficiency);
  };

  const deficiencyCount = () => {
    if (searchText?.length) {
      return filteredDeficiencies.filter(isDeficiencyVisible)?.length;
    } else {
      return deficiencies?.length + activeDeficiencies?.length;
    }
  };

  const onCopy = async (componentVersionUUID: string) => {
    const deficiency = deficiencies.find(def => def.componentVersionUUID === componentVersionUUID);

    if (deficiency) {
      try {
        const cloneDeficiency: Deficiency = {
          ...deficiency,
          [ComponentInfo.NAME]: `Copy of ${deficiency.name}`,
          [ComponentInfo.COMPONENT_FAILURE_ID]: '0',
          [ComponentInfo.PK_DEFICIENCY_UUID]: '',
          [ComponentInfo.COMPONENT_VERSION_UUID]: '',
          [ComponentInfo.COMMENT_INSPECTOR_FIELD]: '',
        };
        await saveDeficiencyList(cloneDeficiency);
        resetFilteredDeficiencies();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error, '~ Error in copying a deficiency ~Deficiencies.tsx');
        // add sentry error
        errorNotification('Deficiency did not copy completely. Please try again.');
      }
    }
  };

  const endAndornmentSearch = () => {
    if (searchText.length !== 0) {
      return (
        <InputAdornment position="end">
          <Icons.XFeather
            color="primary"
            className={classes.searchTextField}
            onClick={() => setSearchText('')}
          />
        </InputAdornment>
      );
    }
  };
  const deficiencyRenderView = useMemo(() => {
    const result = view.condensed
      ? filteredDeficiencies?.map(deficiency => (
          <CondensedView
            key={deficiency.componentVersionUUID}
            deficiency={deficiency}
            onCopy={onCopy}
            setEditDeficiencyDialog={setEditDeficiencyDialog}
            setEditFile={setEditFile}
            repairFactorListData={repairFactorListData}
            setOpenAuditLogsDialog={setOpenAuditLogsDialog}
            setAuditLogsDeficiencyId={setAuditLogsDeficiencyId}
            isEditable={isEditable}
            setInspectionIdForActiveDeficiency={setInspectionIdForActiveDeficiency}
            isActiveDeficiency={tabValue === 1}
            urgencyListData={urgencyListData}
            reloadCondensedView={reloadCondensedView}
          />
        ))
      : filteredDeficiencies?.map(deficiency => (
          <CardView
            key={deficiency.pkDeficiencyUUID}
            deficiency={deficiency}
            onChangeForCardView={onChangeForCardView}
            setEditDeficiencyDialog={setEditDeficiencyDialog}
            setEditFile={setEditFile}
            repairFactorListData={repairFactorListData}
            setOpenAuditLogsDialog={setOpenAuditLogsDialog}
            setAuditLogsDeficiencyId={setAuditLogsDeficiencyId}
            isEditable={isEditable}
            setInspectionIdForActiveDeficiency={setInspectionIdForActiveDeficiency}
            isActiveDeficiency={tabValue === 1}
            conditionRatingListData={conditionRatingListData}
            urgencyListData={urgencyListData}
          />
        ));
    return result;
  }, [filteredDeficiencies, view.condensed, urgencyListData]);

  return (
    <>
      <Card elevation={1} className={classes.card}>
        <Grid container spacing={2}>
          <Grid item container xs={12} alignItems="center">
            <Grid item xs={5}>
              <Typography
                variant="h3"
                fontWeight={300}
              >{`Deficiencies (${deficiencyCount()})`}</Typography>
            </Grid>

            <Grid item xs={7} container justifyContent="flex-end">
              {deficiencyListData?.length > 0 && (
                <Grid item>
                  <TextField
                    inputProps={{ 'data-testid': 'def-search' }}
                    noMinHeight
                    variant="outlined"
                    type="text"
                    size="small"
                    focused
                    startAdornment={
                      <InputAdornment position="start">
                        <Icons.SearchFeather color="primary" />
                      </InputAdornment>
                    }
                    endAdornment={endAndornmentSearch()}
                    value={searchText}
                    placeholder="Search"
                    className={classes.searchField}
                    onChange={handleSearch}
                  />
                </Grid>
              )}
              {isEditable && deficiencyListData?.length > 0 && (
                <Grid item className={classes.dividerGridContainer}>
                  <Divider orientation="vertical" className={classes.deficiencyDivider} />
                </Grid>
              )}
              {isEditable && tabValue === 0 && (
                <Grid item>
                  <Button
                    className={classes.DeficiencyAddButton}
                    noMinHeight
                    color="primary"
                    variant="contained"
                    onClick={() => {
                      setAddFlag(true);
                      setEditDeficiencyDialog(true);
                    }}
                  >
                    <Icons.PlusFeather />
                    <Grid className={classes.DeficiencyAddButtonText}>Add...</Grid>
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Card>

      <DeficienciesSortByPanel
        tabValue={tabValue}
        setValue={setValue}
        resetSortBy={resetSortBy}
        id={id}
        view={view}
        isSortByOpen={isSortByOpen}
        handleSortByClick={handleSortByClick}
        setView={setView}
        activeDeficiencyCount={activeDeficiencies?.length}
        inspectionDeficiencyCount={deficiencies?.length}
      />
      {filteredDeficiencies?.length === 0 && (
        <Card elevation={1} className={classes.noDeficiencyCard}>
          <Card elevation={1} className={classes.card}>
            <Grid container spacing={2}>
              <Grid item container xs={12} alignItems="center">
                {tabValue === 1 ? (
                  <Grid item xs={5}>
                    <Typography variant="h5">No active deficiencies available</Typography>
                  </Grid>
                ) : (
                  <Grid item xs={5}>
                    <Typography variant="h5">No deficiencies available</Typography>
                    <Typography variant="caption" className={classes.noDeficiencyFooter}>
                      Click the Add button to add new deficiency.
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Card>
        </Card>
      )}

      {filteredDeficiencies?.length > 0 && (
        <div
          className={
            configCatFlag.getBlueBannerDisplay === true ||
            configCatFlag.getRedBannerDisplay === true
              ? classes.root1
              : classes.root
          }
          onScroll={handleScroll}
        >
          <Card
            data-testid="deficiency-data-grid"
            elevation={1}
            className={classes.deficiencyDataGrid}
          >
            {deficiencyRenderView}
          </Card>
        </div>
      )}

      {isSortByOpen && (
        <DeficienciesSortBy
          isSortByOpen={isSortByOpen}
          anchorEl={anchorEl}
          handleClose={handleClose}
          view={view}
          isActiveDeficiency={tabValue === 1}
          deficiencyFilters={deficiencyFilters}
          handleFilterChange={handleFilterChange}
        />
      )}

      {editDeficiencyDialog && (
        <EditDeficiency
          open={editDeficiencyDialog}
          onClose={() => {
            setAddFlag(false);
            setEditFile(undefined);
            setEditDeficiencyDialog(false);
            setReloadCondensedView(true);
          }}
          addFlag={addFlag}
          editFile={editFile as Deficiency}
          taskData={taskData}
          isEditable={isEditable}
          isActiveDeficiency={tabValue === 1}
        />
      )}

      <AddPhotoDialog
        onClose={() => setEditFile(undefined)}
        editFile={editFile as Deficiency}
        isEditable={isEditable}
        isActiveDeficiency={tabValue === 1}
      />

      {openAuditLogsDialog && (
        <AuditLogs
          open={openAuditLogsDialog}
          onClose={() => {
            setAuditLogsDeficiencyId(0);
            setOpenAuditLogsDialog(false);
          }}
          title="Deficiency"
          subTitle={tabValue === 1 ? 'Inspection' : 'Task'}
          deficiencyId={auditLogsDeficiencyId}
          isUpdateHistory={false}
          isActiveDeficiency={tabValue === 1}
          inspectionIdForActiveDeficiency={inspectionIdForActiveDeficiency}
        />
      )}
    </>
  );
};

export default observer(Deficiencies);
